'use client';
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import AppImage from '@/components/ui/AppImage';
import { useLanguage } from '../contexts/LanguageContext';

interface SparklineData {
  price: number[];
}

interface CoinMarket {
  id: string;
  symbol: string;
  name: string;
  image: string;
  current_price: number;
  market_cap_rank: number;
  price_change_percentage_1h_in_currency: number;
  price_change_percentage_24h_in_currency: number;
  price_change_percentage_7d_in_currency: number;
  price_change_percentage_24h: number;
  market_cap: number;
  total_volume: number;
  sparkline_in_7d: SparklineData;
}

const TOTAL_RESULTS = 16845;
const ROWS_OPTIONS = [10, 25, 50, 100];

const BUY_COINS = new Set(['bitcoin', 'ethereum', 'bnb', 'ripple', 'solana', 'tron', 'dogecoin', 'cardano', 'avalanche-2', 'polkadot', 'chainlink', 'litecoin', 'uniswap', 'stellar', 'monero']);

// Filter sets per category tab
const HIGHLIGHTS_COINS = new Set([
  'bitcoin', 'ethereum', 'solana', 'bnb', 'ripple', 'dogecoin', 'cardano', 'tron', 'avalanche-2', 'polkadot',
]);

const CATEGORIES_COINS = new Set([
  'chainlink', 'uniswap', 'aave', 'compound-governance-token', 'maker', 'curve-dao-token',
  'the-sandbox', 'decentraland', 'axie-infinity', 'enjincoin',
  'filecoin', 'arweave', 'storj', 'siacoin',
]);

const MOBILE_MINING_COINS = new Set([
  'pi-network', 'electroneum', 'phoneum', 'bee-network', 'time-new-bank',
  'helium', 'iotex', 'iota', 'fetch-ai', 'ocean-protocol',
]);

const QUANTUM_RESISTANT_COINS = new Set([
  'quantum-resistant-ledger', 'iota', 'algorand', 'cardano', 'ethereum',
  'nervos-network', 'mochimo', 'nexus', 'hcash', 'lattice-token',
]);

const SPORTS_COINS = new Set([
  'chiliz', 'socios', 'santos-fc-fan-token', 'paris-saint-germain-fan-token', 'manchester-city-fan-token',
  'alpine-f1-team-fan-token', 'ac-milan-fan-token', 'atletico-de-madrid-fan-token',
  'galatasaray-fan-token', 'lazio-fan-token',
]);

const CATEGORY_TABS = [
  { id: 'all', labelKey: 'markets_tab_all', icon: null, fire: false },
  { id: 'highlights', labelKey: 'markets_tab_highlights', icon: '≡', fire: false },
  { id: 'categories', labelKey: 'markets_tab_categories', icon: '⬡', fire: false },
  { id: 'mobile-mining', labelKey: 'markets_tab_mobile_mining', icon: null, fire: true },
  { id: 'quantum-resistant', labelKey: 'markets_tab_quantum', icon: null, fire: true },
  { id: 'sports', labelKey: 'markets_tab_sports', icon: null, fire: true },
];

function formatPrice(price: number): string {
  if (price >= 1000) {
    return '$' + price.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  } else if (price >= 1) {
    return '$' + price.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 4 });
  } else if (price >= 0.01) {
    return '$' + price.toLocaleString('en-US', { minimumFractionDigits: 4, maximumFractionDigits: 6 });
  } else {
    return '$' + price.toLocaleString('en-US', { minimumFractionDigits: 6, maximumFractionDigits: 8 });
  }
}

function formatLargeNumber(num: number): string {
  if (num >= 1_000_000_000) {
    return '$' + (num / 1_000_000_000).toFixed(2) + 'B';
  } else if (num >= 1_000_000) {
    return '$' + (num / 1_000_000).toFixed(2) + 'M';
  } else {
    return '$' + num.toLocaleString('en-US');
  }
}

function formatPercent(val: number | null | undefined): { text: string; positive: boolean } {
  if (val === null || val === undefined || isNaN(val)) {
    return { text: '0.0%', positive: true };
  }
  const positive = val >= 0;
  return { text: Math.abs(val).toFixed(1) + '%', positive };
}

function Sparkline({ data, positive }: { data: number[]; positive: boolean }) {
  if (!data || data.length < 2) {
    return <div className="w-24 h-10" />;
  }

  const width = 96;
  const height = 40;
  const min = Math.min(...data);
  const max = Math.max(...data);
  const range = max - min || 1;

  const points = data.map((val, i) => {
    const x = (i / (data.length - 1)) * width;
    const y = height - ((val - min) / range) * height;
    return `${x},${y}`;
  }).join(' ');

  const color = positive ? '#22c55e' : '#ef4444';

  return (
    <svg width={width} height={height} viewBox={`0 0 ${width} ${height}`} className="overflow-visible">
      <polyline
        points={points}
        fill="none"
        stroke={color}
        strokeWidth="1.5"
        strokeLinejoin="round"
        strokeLinecap="round"
      />
    </svg>
  );
}

function getPageNumbers(currentPage: number, totalPages: number): (number | '...')[] {
  if (totalPages <= 7) {
    return Array.from({ length: totalPages }, (_, i) => i + 1);
  }

  const pages: (number | '...')[] = [];

  if (currentPage <= 4) {
    pages.push(1, 2, 3, 4, 5, '...', totalPages);
  } else if (currentPage >= totalPages - 3) {
    pages.push(1, '...', totalPages - 4, totalPages - 3, totalPages - 2, totalPages - 1, totalPages);
  } else {
    pages.push(1, '...', currentPage - 1, currentPage, currentPage + 1, '...', totalPages);
  }

  return pages;
}

export default function MarketsTable() {
  const [coins, setCoins] = useState<CoinMarket[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [favorites, setFavorites] = useState<Set<string>>(new Set());
  const [activeTab, setActiveTab] = useState('all');
  const { t } = useLanguage();

  // suppress unused warning
  void coins;

  const fetchCoins = useCallback(async (page: number, perPage: number) => {
    try {
      setLoading(true);
      setError(null);

      const response = await fetch(`/api/markets?page=${page}&per_page=${perPage}`);
      if (!response.ok) {
        throw new Error('Failed to fetch market data');
      }
      const data = await response.json();
      if (!Array.isArray(data)) {
        throw new Error(data?.error || 'Invalid market data received');
      }
      setCoins(data as CoinMarket[]);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchCoins(currentPage, rowsPerPage);
  }, [fetchCoins, currentPage, rowsPerPage]);

  // Filter coins based on active tab
  const filteredCoins = React.useMemo(() => {
    switch (activeTab) {
      case 'highlights':
        return coins.filter(c => HIGHLIGHTS_COINS.has(c.id));
      case 'categories':
        return coins.filter(c => CATEGORIES_COINS.has(c.id));
      case 'mobile-mining':
        return coins.filter(c => MOBILE_MINING_COINS.has(c.id));
      case 'quantum-resistant':
        return coins.filter(c => QUANTUM_RESISTANT_COINS.has(c.id));
      case 'sports':
        return coins.filter(c => SPORTS_COINS.has(c.id));
      default:
        return coins;
    }
  }, [coins, activeTab]);

  const totalPages = Math.ceil(TOTAL_RESULTS / rowsPerPage);
  const startResult = (currentPage - 1) * rowsPerPage + 1;
  const endResult = Math.min(currentPage * rowsPerPage, TOTAL_RESULTS);

  const handlePageChange = (page: number) => {
    if (page < 1 || page > totalPages) return;
    setCurrentPage(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleRowsChange = (newRows: number) => {
    setRowsPerPage(newRows);
    setCurrentPage(1);
  };

  const toggleFavorite = (id: string) => {
    setFavorites(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const pageNumbers = getPageNumbers(currentPage, totalPages);

  return (
    <section className="bg-black w-full" style={{ fontFamily: "'Inter', 'Segoe UI', system-ui, -apple-system, sans-serif" }}>
      <div className="max-w-7xl mx-auto px-2 sm:px-4">

        {/* Category Tab Bar */}
        <div className="flex items-center justify-between border-b border-[#1e2a4a] overflow-x-auto scrollbar-none" style={{ WebkitOverflowScrolling: 'touch' }}>
          <div className="flex items-center gap-0.5 flex-shrink-0">
            {CATEGORY_TABS.map(tab => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-1 px-2 sm:px-3 py-2 sm:py-2.5 text-[10px] sm:text-xs font-medium whitespace-nowrap transition-colors border-b-2 ${
                  activeTab === tab.id
                    ? 'border-green-500 text-green-400 bg-green-500/10' :'border-transparent text-slate-400 hover:text-slate-200 hover:bg-white/[0.04]'
                }`}
              >
                {tab.fire && <span className="text-xs sm:text-sm leading-none">🔥</span>}
                {tab.icon && !tab.fire && (
                  <span className="text-slate-500 text-[10px] sm:text-xs">{tab.icon}</span>
                )}
                {tab.id === 'all' && activeTab === 'all' && (
                  <span className="w-1.5 h-1.5 rounded-full bg-green-400 inline-block" />
                )}
                {t(tab.labelKey)}
              </button>
            ))}
          </div>
          <button className="flex items-center gap-1 px-2 sm:px-3 py-1.5 sm:py-2 ml-1 rounded-lg border border-[#2a3a5c] text-slate-400 hover:text-slate-200 hover:border-slate-500 text-[10px] sm:text-xs font-medium transition-colors whitespace-nowrap flex-shrink-0">
            <span className="text-xs sm:text-sm">✦</span>
            <span className="hidden sm:inline">{t('markets_customize')}</span>
          </button>
        </div>

        {/* Table Header Bar */}
        <div className="flex items-center justify-between px-2 sm:px-4 py-2 sm:py-2.5 border-b border-[#1e2a4a]">
          <div className="flex items-center gap-2">
            <span className="text-white font-semibold text-[10px] sm:text-xs tracking-tight">
              {activeTab === 'all' ? t('markets_label_all') :
               activeTab === 'highlights' ? t('markets_label_highlights') :
               activeTab === 'categories' ? t('markets_label_categories') :
               activeTab === 'mobile-mining' ? t('markets_label_mobile_mining') :
               activeTab === 'quantum-resistant' ? t('markets_label_quantum') :
               activeTab === 'sports' ? t('markets_label_sports') : t('markets_label_all')}
            </span>
            <span className="px-2 py-0.5 rounded-full bg-purple-500/20 text-purple-400 text-[9px] sm:text-[10px] font-medium">
              {loading ? '...' : activeTab === 'all' ? `${TOTAL_RESULTS.toLocaleString()}+ ${t('markets_coins_suffix')}` : `${filteredCoins.length} ${t('markets_coins_suffix')}`}
            </span>
          </div>
          <button
            onClick={() => fetchCoins(currentPage, rowsPerPage)}
            disabled={loading}
            className="flex items-center gap-1 px-2 sm:px-2.5 py-1 sm:py-1.5 rounded-full bg-white/5 hover:bg-white/10 text-slate-400 hover:text-white text-[9px] sm:text-[10px] font-medium transition-colors disabled:opacity-50"
          >
            <svg className={`w-2.5 h-2.5 sm:w-3 sm:h-3 ${loading ? 'animate-spin' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
            </svg>
            {t('markets_refresh')}
          </button>
        </div>

        {/* Loading State */}
        {loading && (
          <div className="flex flex-col items-center justify-center py-20 gap-4">
            <div className="w-10 h-10 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
            <p className="text-slate-400 text-sm">{t('markets_fetching')}</p>
          </div>
        )}

        {/* Error State */}
        {!loading && error && (
          <div className="flex flex-col items-center justify-center py-20 gap-4">
            <div className="w-12 h-12 rounded-full bg-red-500/20 flex items-center justify-center">
              <svg className="w-6 h-6 text-red-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
            </div>
            <p className="text-red-400 text-sm">{error}</p>
            <button
              onClick={() => fetchCoins(currentPage, rowsPerPage)}
              className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm rounded-lg transition-colors"
            >
              {t('markets_try_again')}
            </button>
          </div>
        )}

        {/* Table */}
        {!loading && !error && (
          <>
            {/* Empty state for filtered tabs */}
            {filteredCoins.length === 0 && activeTab !== 'all' && (
              <div className="flex flex-col items-center justify-center py-16 gap-3">
                <div className="w-12 h-12 rounded-full bg-slate-800 flex items-center justify-center">
                  <span className="text-2xl">🔍</span>
                </div>
                <p className="text-slate-400 text-sm">{t('markets_no_coins')}</p>
                <p className="text-slate-600 text-xs">{t('markets_no_coins_hint')}</p>
              </div>
            )}

            {filteredCoins.length > 0 && (
            <div className="overflow-x-auto">
              <table className="w-full" style={{ minWidth: '480px' }}>
                <thead>
                  <tr className="border-b border-[#1e2a4a]">
                    <th className="w-8 px-3 py-3"></th>
                    <th className="w-10 px-2 py-3 text-left">
                      <span className="flex items-center gap-1 cursor-pointer select-none text-slate-500 text-[11px] font-semibold uppercase tracking-wider">
                        # <span className="text-slate-600 text-[9px]">▲</span>
                      </span>
                    </th>
                    <th className="px-3 py-3 text-left">
                      <span className="text-slate-500 text-[11px] font-semibold uppercase tracking-wider">{t('markets_col_coin')}</span>
                    </th>
                    <th className="w-16 px-2 py-3 hidden sm:table-cell"></th>
                    <th className="px-3 py-3 text-right">
                      <span className="text-slate-500 text-[11px] font-semibold uppercase tracking-wider">{t('markets_col_price')}</span>
                    </th>
                    <th className="px-3 py-3 text-right hidden sm:table-cell">
                      <span className="text-slate-500 text-[11px] font-semibold uppercase tracking-wider">{t('markets_col_1h')}</span>
                    </th>
                    <th className="px-3 py-3 text-right">
                      <span className="text-slate-500 text-[11px] font-semibold uppercase tracking-wider">{t('markets_col_24h')}</span>
                    </th>
                    <th className="px-3 py-3 text-right hidden sm:table-cell">
                      <span className="text-slate-500 text-[11px] font-semibold uppercase tracking-wider">{t('markets_col_7d')}</span>
                    </th>
                    <th className="px-3 py-3 text-right hidden lg:table-cell">
                      <span className="text-slate-500 text-[11px] font-semibold uppercase tracking-wider">{t('markets_col_volume')}</span>
                    </th>
                    <th className="px-3 py-3 text-right hidden md:table-cell">
                      <span className="text-slate-500 text-[11px] font-semibold uppercase tracking-wider">{t('markets_col_marketcap')}</span>
                    </th>
                    <th className="px-3 py-3 text-right hidden xl:table-cell">
                      <span className="text-slate-500 text-[11px] font-semibold uppercase tracking-wider">{t('markets_col_chart')}</span>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {filteredCoins.map((coin, index) => {
                    const rank = (currentPage - 1) * rowsPerPage + index + 1;
                    const isFav = favorites.has(coin.id);
                    const hasBuy = BUY_COINS.has(coin.id);

                    const change1h = formatPercent(coin.price_change_percentage_1h_in_currency);
                    const change24h = formatPercent(coin.price_change_percentage_24h_in_currency ?? coin.price_change_percentage_24h);
                    const change7d = formatPercent(coin.price_change_percentage_7d_in_currency);

                    const sparklineData = coin.sparkline_in_7d?.price ?? [];
                    const sampledSparkline = sparklineData.length > 50
                      ? sparklineData.filter((_: number, i: number) => i % Math.floor(sparklineData.length / 50) === 0)
                      : sparklineData;

                    return (
                      <tr
                        key={coin.id}
                        className="border-b border-[#1e2a4a] hover:bg-white/[0.03] transition-colors"
                        style={{ height: '52px' }}
                      >
                        {/* Star */}
                        <td className="px-2 sm:px-3 text-center">
                          <button
                            onClick={() => toggleFavorite(coin.id)}
                            className={`text-base sm:text-lg transition-colors ${isFav ? 'text-yellow-400' : 'text-slate-600 hover:text-slate-400'}`}
                            aria-label={isFav ? 'Remove from favorites' : 'Add to favorites'}
                          >
                            {isFav ? '★' : '☆'}
                          </button>
                        </td>

                        {/* Rank */}
                        <td className="px-1 sm:px-2">
                          <span className="text-slate-400 text-[11px] sm:text-sm tabular-nums">{rank}</span>
                        </td>

                        {/* Coin */}
                        <td className="px-2 sm:px-3">
                          <div className="flex items-center gap-2 sm:gap-3">
                            <div className="w-6 h-6 sm:w-8 sm:h-8 rounded-full overflow-hidden flex-shrink-0 bg-white/5">
                              <AppImage
                                src={coin.image}
                                alt={`${coin.name} logo`}
                                width={32}
                                height={32}
                                className="w-full h-full object-contain"
                              />
                            </div>
                            <div className="flex items-baseline gap-1 sm:gap-1.5">
                              <span className="text-white text-[11px] sm:text-sm font-bold tracking-tight">{coin.name}</span>
                              <span className="text-slate-500 text-[9px] sm:text-[11px] font-medium uppercase tracking-wide">{coin.symbol}</span>
                            </div>
                          </div>
                        </td>

                        {/* Buy Button */}
                        <td className="px-1 sm:px-2 hidden sm:table-cell">
                          {hasBuy && (
                            <button className="px-1.5 sm:px-2.5 py-0.5 sm:py-1 rounded border border-green-500 text-green-400 text-[9px] sm:text-xs font-semibold hover:bg-green-500/10 transition-colors whitespace-nowrap tracking-wide">
                              {t('markets_buy')}
                            </button>
                          )}
                        </td>

                        {/* Price */}
                        <td className="px-2 sm:px-3 text-right">
                          <span className="text-white text-[11px] sm:text-sm font-semibold tabular-nums" style={{ fontVariantNumeric: 'tabular-nums' }}>
                            {formatPrice(coin.current_price)}
                          </span>
                        </td>

                        {/* 1h % */}
                        <td className="px-2 sm:px-3 text-right hidden sm:table-cell">
                          <span className={`text-[11px] sm:text-sm font-semibold tabular-nums flex items-center justify-end gap-0.5 ${change1h.positive ? 'text-green-400' : 'text-red-400'}`}>
                            <span className="text-[9px] sm:text-[10px]">{change1h.positive ? '▲' : '▼'}</span>
                            {change1h.text}
                          </span>
                        </td>

                        {/* 24h % */}
                        <td className="px-2 sm:px-3 text-right">
                          <span className={`text-[11px] sm:text-sm font-semibold tabular-nums flex items-center justify-end gap-0.5 ${change24h.positive ? 'text-green-400' : 'text-red-400'}`}>
                            <span className="text-[9px] sm:text-[10px]">{change24h.positive ? '▲' : '▼'}</span>
                            {change24h.text}
                          </span>
                        </td>

                        {/* 7d % */}
                        <td className="px-2 sm:px-3 text-right hidden sm:table-cell">
                          <span className={`text-[11px] sm:text-sm font-semibold tabular-nums flex items-center justify-end gap-0.5 ${change7d.positive ? 'text-green-400' : 'text-red-400'}`}>
                            <span className="text-[9px] sm:text-[10px]">{change7d.positive ? '▲' : '▼'}</span>
                            {change7d.text}
                          </span>
                        </td>

                        {/* 24h Volume */}
                        <td className="px-2 sm:px-3 text-right hidden lg:table-cell">
                          <span className="text-slate-300 text-[11px] sm:text-sm tabular-nums font-medium">{formatLargeNumber(coin.total_volume)}</span>
                        </td>

                        {/* Market Cap */}
                        <td className="px-2 sm:px-3 text-right hidden md:table-cell">
                          <span className="text-slate-300 text-[11px] sm:text-sm tabular-nums font-medium">{formatLargeNumber(coin.market_cap)}</span>
                        </td>

                        {/* Sparkline */}
                        <td className="px-2 sm:px-3 text-right hidden xl:table-cell">
                          <div className="flex justify-end">
                            <Sparkline data={sampledSparkline} positive={change7d.positive} />
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            )}

            {/* Pagination Bar - only show for 'all' tab */}
            {activeTab === 'all' && (
            <div className="flex items-center justify-between px-4 py-4 border-t border-[#1e2a4a] flex-wrap gap-3">
              {/* Left: Showing X to Y of Z results */}
              <span className="text-slate-500 text-xs tabular-nums whitespace-nowrap">
                {t('markets_showing')} {startResult.toLocaleString()} {t('markets_to')} {endResult.toLocaleString()} {t('markets_of')} {TOTAL_RESULTS.toLocaleString()} {t('markets_results')}
              </span>

              {/* Center: Page navigation */}
              <div className="flex items-center gap-1">
                {/* Previous arrow */}
                <button
                  onClick={() => handlePageChange(currentPage - 1)}
                  disabled={currentPage === 1}
                  className="w-8 h-8 flex items-center justify-center rounded text-slate-400 hover:text-white hover:bg-white/10 transition-colors disabled:opacity-30 disabled:cursor-not-allowed text-base"
                  aria-label="Previous page"
                >
                  ‹
                </button>

                {/* Page numbers */}
                {pageNumbers.map((page, idx) =>
                  page === '...' ? (
                    <span key={`ellipsis-${idx}`} className="w-8 h-8 flex items-center justify-center text-slate-500 text-sm select-none">
                      ...
                    </span>
                  ) : (
                    <button
                      key={page}
                      onClick={() => handlePageChange(page as number)}
                      className={`w-8 h-8 flex items-center justify-center rounded text-sm font-medium transition-colors ${
                        currentPage === page
                          ? 'bg-white text-black font-bold' :'text-slate-400 hover:text-white hover:bg-white/10'
                      }`}
                    >
                      {page}
                    </button>
                  )
                )}

                {/* Next arrow */}
                <button
                  onClick={() => handlePageChange(currentPage + 1)}
                  disabled={currentPage === totalPages}
                  className="w-8 h-8 flex items-center justify-center rounded text-slate-400 hover:text-white hover:bg-white/10 transition-colors disabled:opacity-30 disabled:cursor-not-allowed text-base"
                  aria-label="Next page"
                >
                  ›
                </button>
              </div>

              {/* Right: Rows dropdown + scroll to top */}
              <div className="flex items-center gap-2">
                <span className="text-slate-400 text-xs font-medium">{t('markets_rows')}</span>
                <div className="relative">
                  <select
                    value={rowsPerPage}
                    onChange={(e) => handleRowsChange(Number(e.target.value))}
                    className="appearance-none bg-black border border-[#2a3a5c] text-white text-sm font-semibold pl-3 pr-7 py-1.5 rounded cursor-pointer hover:border-slate-500 transition-colors focus:outline-none focus:border-slate-400 tabular-nums"
                  >
                    {ROWS_OPTIONS.map(opt => (
                      <option key={opt} value={opt} className="bg-[#111]">{opt}</option>
                    ))}
                  </select>
                  <div className="pointer-events-none absolute inset-y-0 right-2 flex items-center">
                    <svg className="w-3 h-3 text-slate-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M19 9l-7 7-7-7" />
                    </svg>
                  </div>
                </div>

                {/* Scroll to top */}
                <button
                  onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
                  className="w-8 h-8 flex items-center justify-center rounded border border-[#2a3a5c] text-slate-400 hover:text-white hover:border-slate-500 transition-colors"
                  aria-label="Scroll to top"
                >
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
                  </svg>
                </button>
              </div>
            </div>
            )}
          </>
        )}
      </div>
    </section>
  );
}
